"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HDSModel = void 0;
const errors_ts_1 = require("../errors.js");
const utils_ts_1 = require("../utils.js");
const HDSModel_Streams_ts_1 = require("./HDSModel-Streams.js");
const HDSModel_Authorizations_ts_1 = require("./HDSModel-Authorizations.js");
const HDSModel_ItemsDefs_ts_1 = require("./HDSModel-ItemsDefs.js");
const HDSModel_EventTypes_ts_1 = require("./HDSModel-EventTypes.js");
const HDSModel_Datasources_ts_1 = require("./HDSModel-Datasources.js");
const HDSModel_Conversions_ts_1 = require("./HDSModel-Conversions.js");
const HDSModel_Converters_ts_1 = require("./HDSModel-Converters.js");
const HDSModel_Preferred_ts_1 = require("./HDSModel-Preferred.js");
const HDSModel_AppStreams_ts_1 = require("./HDSModel-AppStreams.js");
const HDSModel_Overload_ts_1 = require("./HDSModel-Overload.js");
class HDSModel {
    /**
     * JSON definition file URL.
     * Should come from service/info assets.hds-model
     */
    #modelUrl;
    /** RAW content of model definitions */
    #modelData;
    /** Service-info assets map (e.g. { datasets: 'https://...', ... }) */
    #assets;
    /**
     * Map of properties loaded "on demand"
     */
    laziliyLoadedMap = {};
    /**
     * @param modelUrl - JSON definition file URL. Should come from service/info assets.hds-model
     */
    constructor(modelUrl) {
        this.#modelUrl = modelUrl;
        this.laziliyLoadedMap = {};
        this.#modelData = null;
        this.#assets = {};
    }
    /** Service-info assets used for resolving datasource endpoints */
    get assets() {
        return this.#assets;
    }
    set assets(value) {
        this.#assets = value || {};
    }
    get isLoaded() {
        return !!this.#modelData;
    }
    /** The URL the model was loaded from */
    get modelUrl() {
        return this.#modelUrl;
    }
    /**
     * Load model definitions.
     *
     * @param modelUrl - JSON definition URL (defaults to the one set at construct time)
     * @param overload - Optional {@link HDSModelOverload} merged into the loaded
     *   model BEFORE freezing. Lets apps add itemDefs / streams / eventTypes /
     *   settings or refine translations & default `repeatable` values without
     *   forking the data-model. See `HDSModel-Overload.ts` for the policy.
     */
    async load(modelUrl = null, overload = null) {
        if (modelUrl) {
            this.#modelUrl = modelUrl;
        }
        const response = await fetch(this.#modelUrl);
        const resultText = await response.text();
        const result = JSON.parse(resultText);
        this.loadFromObject(result, overload);
    }
    /**
     * Load model from an in-memory object (skips fetch). Useful when the
     * pack.json content is already in memory — tests, embedded apps, or
     * environments where fetch can't reach the model URL (e.g. Node with a
     * file:// URL, which Node's fetch does not yet implement).
     */
    loadFromObject(data, overload = null) {
        if (overload) {
            (0, HDSModel_Overload_ts_1.validateOverload)(data, overload);
            (0, HDSModel_Overload_ts_1.applyOverload)(data, overload);
        }
        this.#modelData = data;
        // add key to items before freezing;
        for (const [key, item] of Object.entries(this.#modelData.items)) {
            item.key = key;
        }
        // make sure it cannot be modified
        (0, utils_ts_1.deepFreeze)(this.#modelData);
    }
    /** RAW model data */
    get modelData() {
        if (!this.isLoaded)
            throwNotLoadedError();
        return this.#modelData;
    }
    get itemsDefs() {
        if (!this.isLoaded)
            throwNotLoadedError();
        if (!this.laziliyLoadedMap.itemsDefs) {
            this.laziliyLoadedMap.itemsDefs = new HDSModel_ItemsDefs_ts_1.HDSModelItemsDefs(this);
        }
        return this.laziliyLoadedMap.itemsDefs;
    }
    get streams() {
        if (!this.isLoaded)
            throwNotLoadedError();
        if (!this.laziliyLoadedMap.streams) {
            this.laziliyLoadedMap.streams = new HDSModel_Streams_ts_1.HDSModelStreams(this);
        }
        return this.laziliyLoadedMap.streams;
    }
    get authorizations() {
        if (!this.isLoaded)
            throwNotLoadedError();
        if (!this.laziliyLoadedMap.authorizations) {
            this.laziliyLoadedMap.authorizations = new HDSModel_Authorizations_ts_1.HDSModelAuthorizations(this);
        }
        return this.laziliyLoadedMap.authorizations;
    }
    get eventTypes() {
        if (!this.isLoaded)
            throwNotLoadedError();
        if (!this.laziliyLoadedMap.eventTypes) {
            this.laziliyLoadedMap.eventTypes = new HDSModel_EventTypes_ts_1.HDSModelEventTypes(this);
        }
        return this.laziliyLoadedMap.eventTypes;
    }
    get datasources() {
        if (!this.isLoaded)
            throwNotLoadedError();
        if (!this.laziliyLoadedMap.datasources) {
            this.laziliyLoadedMap.datasources = new HDSModel_Datasources_ts_1.HDSModelDatasources(this);
        }
        return this.laziliyLoadedMap.datasources;
    }
    get conversions() {
        if (!this.isLoaded)
            throwNotLoadedError();
        if (!this.laziliyLoadedMap.conversions) {
            this.laziliyLoadedMap.conversions = new HDSModel_Conversions_ts_1.HDSModelConversions(this);
        }
        return this.laziliyLoadedMap.conversions;
    }
    get converters() {
        if (!this.isLoaded)
            throwNotLoadedError();
        if (!this.laziliyLoadedMap.converters) {
            this.laziliyLoadedMap.converters = new HDSModel_Converters_ts_1.HDSModelConverters(this);
        }
        return this.laziliyLoadedMap.converters;
    }
    get preferred() {
        if (!this.isLoaded)
            throwNotLoadedError();
        if (!this.laziliyLoadedMap.preferred) {
            this.laziliyLoadedMap.preferred = new HDSModel_Preferred_ts_1.HDSModelPreferred(this);
        }
        return this.laziliyLoadedMap.preferred;
    }
    get appStreams() {
        if (!this.isLoaded)
            throwNotLoadedError();
        if (!this.laziliyLoadedMap.appStreams) {
            this.laziliyLoadedMap.appStreams = new HDSModel_AppStreams_ts_1.HDSModelAppStreams(this);
        }
        return this.laziliyLoadedMap.appStreams;
    }
}
exports.HDSModel = HDSModel;
function throwNotLoadedError() {
    throw new errors_ts_1.HDSLibError('Model not loaded call `HDSLib.initHDSModel()` or `await model.load()` first.');
}
